import { TokenPairSchema, toProblem, type ReasonCode } from '@paguz/contracts';
import type { z } from 'zod';
import { session } from '../auth/session.ts';
import { config } from '../config.ts';
import { ApiError, networkError, problemFromResponse } from './errors.ts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  /** Cuerpo sin serializar (multipart `FormData`, audio `Blob`); el navegador pone el `Content-Type`. */
  rawBody?: FormData | Blob;
  /** `Content-Type` explícito para `rawBody` de tipo `Blob` (p. ej. `audio/wav`). */
  contentType?: string;
  query?: Readonly<Record<string, QueryValue>>;
  /** Header `Idempotency-Key` (obligatorio en POST que crean o mueven dinero). */
  idempotencyKey?: string;
  /** Envía el token del cliente (por defecto `true`). */
  auth?: boolean;
  accept?: string;
  signal?: AbortSignal;
}

/** Une la URL base configurable con la ruta `/v1/...`. */
export function apiUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${config.apiBaseUrl}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? url : `${url}?${qs}`;
}

/** 401 que significan sesión inválida (se rota el refresh o se cierra la sesión). */
const SESSION_CODES: ReadonlySet<ReasonCode> = new Set<ReasonCode>([
  'PZ-API-401',
  'PZ-AUTH-004',
  'PZ-AUTH-007',
  'PZ-AUTH-008',
]);

let refreshInFlight: Promise<boolean> | null = null;

/** Rota el refresh token (una sola petición aunque fallen varias a la vez). */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = session.getRefreshToken();
    if (refreshToken === null) {
      session.clear();
      return false;
    }
    try {
      const response = await fetch(apiUrl('/v1/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        session.clear();
        return false;
      }
      session.setTokens(TokenPairSchema.parse(await response.json()));
      return true;
    } catch {
      // Sin red: conserva el refresh token para intentarlo después, pero sin sesión activa.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers({ Accept: options.accept ?? 'application/json' });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  else if (options.contentType !== undefined) headers.set('Content-Type', options.contentType);
  if (options.idempotencyKey !== undefined) headers.set('Idempotency-Key', options.idempotencyKey);
  const token = session.getAccessToken();
  if ((options.auth ?? true) && token !== null) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** `fetch` autenticado: reintenta una vez tras rotar el refresh token si la API responde 401. */
export async function apiFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  const send = () =>
    fetch(apiUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: buildHeaders(options),
      body: options.rawBody ?? (options.body === undefined ? null : JSON.stringify(options.body)),
      signal: options.signal ?? null,
    });

  const authed = options.auth ?? true;
  let response: Response;
  try {
    response = await send();
    if (response.status === 401 && authed && session.getRefreshToken() !== null) {
      const problem = problemFromResponse(401, await readBody(response.clone()));
      if (SESSION_CODES.has(problem.reasonCode) && (await refreshSession())) {
        response = await send();
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw networkError(error);
  }

  if (!response.ok) {
    const problem = problemFromResponse(response.status, await readBody(response));
    // Un 401 de negocio (p. ej. OTP incorrecto, PZ-SCA-001) no cierra la sesión.
    if (response.status === 401 && authed && SESSION_CODES.has(problem.reasonCode)) {
      session.clear();
    }
    throw new ApiError(problem);
  }
  return response;
}

/** Petición JSON validada con el esquema Zod del contrato. */
export async function apiRequest<S extends z.ZodType>(
  path: string,
  schema: S,
  options: RequestOptions = {},
): Promise<z.output<S>> {
  const response = await apiFetch(path, options);
  const body = await readBody(response);
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiError({
      ...toProblem('PZ-SYS-001', { detail: `Respuesta fuera de contrato en ${path}` }),
    });
  }
  return parsed.data;
}

/** Petición sin cuerpo de respuesta (204). */
export async function apiSend(path: string, options: RequestOptions = {}): Promise<void> {
  await apiFetch(path, options);
}
